// frontend/src/utils/logger.js
// ✅ Browser-safe logger that sends logs to backend Winston
// Provides file-scoped logging with automatic context for easy debugging

function getBackendUrl() {
  return process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";
}

function tsIST() {
  return new Date().toLocaleString("en-IN", { 
    timeZone: "Asia/Kolkata", 
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

// Send log to backend (fire-and-forget)
function sendToBackend(level, message, data, file, method) {
  if (typeof window === "undefined") return;
  
  const url = `${getBackendUrl()}/logs`;
  
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ 
      level, 
      message, 
      data: data || null,
      file,
      method,
      timestamp: new Date().toISOString()
    })
  }).catch(err => {
    console.error("Failed to send log to backend:", err.message);
  });
}

// Base emit function
function emit(level, message, data, file, method) {
  const timestamp = tsIST();
  const fileContext = file && method ? `[${file}][${method}]` : '';
  const formatted = `[${timestamp}] [${level.toUpperCase()}] ${fileContext} ${message}`;
  
  // Console output with appropriate method
  switch(level) {
    case 'error':
      console.error(formatted, data || '');
      break;
    case 'warn':
      console.warn(formatted, data || '');
      break;
    case 'debug':
      console.debug(formatted, data || '');
      break;
    default:
      console.log(formatted, data || '');
  }
  
  // Send to backend
  sendToBackend(level, message, data, file, method);
}

// Helper to extract file name from path
function getFileName(filePath) {
  if (!filePath) return null;
  const parts = filePath.split('/');
  const fileName = parts[parts.length - 1];
  return fileName.replace(/\.(js|jsx|ts|tsx)$/, '');
}

// Create file-scoped logger with automatic context
export const createLogger = (filePath) => {
  const fileName = getFileName(filePath);
  
  return {
    info: (method, message, data = null) => {
      emit('info', message, data, fileName, method);
    },
    warn: (method, message, data = null) => {
      emit('warn', message, data, fileName, method);
    },
    error: (method, message, error = null) => {
      const errorData = error ? {
        message: error.message || error,
        stack: error.stack
      } : null;
      emit('error', message, errorData, fileName, method);
    },
    debug: (method, message, data = null) => {
      emit('debug', message, data, fileName, method);
    },
    // Shorthand for entry/exit
    entry: (method, params = {}) => {
      emit('info', `▶️ Entry`, params, fileName, method);
    },
    exit: (method, result = {}) => {
      emit('info', `✅ Exit`, result, fileName, method);
    }
  };
};

// Legacy logger (backwards compatible)
export const logger = {
  log: (message, data = null) => emit('info', message, data),
  info: (message, data = null) => emit('info', message, data),
  warn: (message, data = null) => emit('warn', message, data),
  error: (message, error = null) => {
    const errorData = error ? {
      message: error.message || error,
      stack: error.stack
    } : null;
    emit('error', message, errorData);
  },
  debug: (message, data = null) => emit('debug', message, data)
};
